import React from "react";
import { Link } from "react-router-dom";
import { Flag, ArrowLeft } from "@phosphor-icons/react";

function LegalLayout({ title, eyebrow, children }) {
  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#0F2537]">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#0F2537] flex items-center justify-center">
              <Flag weight="fill" className="text-[#E05D3A]" size={20} />
            </div>
            <div className="leading-none">
              <div className="font-heading font-black text-lg">Bula Golf</div>
              <div className="label-eyebrow !text-[0.55rem]">Fiji Handicap Tracker</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold text-[#0F2537]/70 hover:text-[#0F2537]">
            <ArrowLeft size={14} weight="bold" /> Back
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <span className="label-eyebrow">{eyebrow}</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">{title}</h1>
        <div className="prose prose-sm md:prose-base mt-6 max-w-none text-[#0F2537]/80 space-y-4 leading-relaxed">
          {children}
        </div>
      </main>
    </div>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" eyebrow="Legal">
      <p><b>Last updated:</b> {new Date().toLocaleDateString()}.</p>
      <p>These terms govern your use of Bula Golf (the &ldquo;Service&rdquo;), operated from Fiji.</p>

      <h2 className="font-heading font-bold text-xl mt-6">1. The Service</h2>
      <p>Bula Golf is a handicap tracking, scoring and booking platform for golfers in Fiji and visitors playing Fijian courses. The Service includes a free tier and a paid subscription called <b>Bula Pro</b>.</p>

      <h2 className="font-heading font-bold text-xl mt-6">2. Accounts</h2>
      <p>You must provide accurate registration information and keep your password secure. You are responsible for activity on your account.</p>

      <h2 className="font-heading font-bold text-xl mt-6">3. Bula Pro subscription</h2>
      <ul className="list-disc ml-5 space-y-1">
        <li><b>Pricing:</b> FJ$19 per month or FJ$169 per year. Prices may change with 30 days&rsquo; notice; existing subscribers keep the rate until renewal.</li>
        <li><b>Billing:</b> Payments are processed by Stripe. Each purchase grants Pro access for the listed period (30 or 365 days). Bula Pro is currently sold as one-time period purchases — there is <b>no automatic renewal</b>.</li>
        <li><b>7-day money-back guarantee:</b> If you cancel within 7 days of payment we will refund the full amount and revoke Pro access. After 7 days the purchase is non-refundable, but you keep Pro through the period you paid for.</li>
      </ul>

      <h2 className="font-heading font-bold text-xl mt-6">4. Bookings</h2>
      <p>Tee-time and resort bookings made through Bula Golf are placed with the relevant course or property. Bula Golf is not a party to the booking and cannot guarantee availability, refunds or quality of service at the property — those are the responsibility of the operator.</p>

      <h2 className="font-heading font-bold text-xl mt-6">5. Acceptable use</h2>
      <p>You agree not to: misuse the Service, attempt to circumvent paywalls, scrape data at high volume, impersonate others, or upload content you don&rsquo;t have the right to share.</p>

      <h2 className="font-heading font-bold text-xl mt-6">6. Liability</h2>
      <p>The Service is provided &ldquo;as is.&rdquo; To the extent permitted by Fijian law, Bula Golf&rsquo;s liability is limited to the amount you paid us in the 12 months before the claim.</p>

      <h2 className="font-heading font-bold text-xl mt-6">7. Contact</h2>
      <p>Questions? Email <a href="mailto:support@bulagolf.fj" className="text-[#E05D3A] font-bold">support@bulagolf.fj</a>.</p>
    </LegalLayout>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" eyebrow="Legal">
      <p><b>Last updated:</b> {new Date().toLocaleDateString()}.</p>
      <p>We take your privacy seriously. This policy explains what we collect and why.</p>

      <h2 className="font-heading font-bold text-xl mt-6">What we collect</h2>
      <ul className="list-disc ml-5 space-y-1">
        <li><b>Account info:</b> email, name, password (stored as a one-way bcrypt hash — we never see your password).</li>
        <li><b>Round data:</b> scores, putts, fairway/GIR flags, GPS shot positions (only when you use the Shot Tracker).</li>
        <li><b>Booking info:</b> dates, course, resort, party size — needed to confirm the booking with the property.</li>
        <li><b>Payment info:</b> Stripe collects card data directly. We only store the transaction reference, amount and date — never card numbers or CVC.</li>
        <li><b>Device/analytics:</b> we use PostHog to understand which screens are useful. We do not sell data.</li>
      </ul>

      <h2 className="font-heading font-bold text-xl mt-6">How we use it</h2>
      <ul className="list-disc ml-5 space-y-1">
        <li>Show you your scores, handicap and stats.</li>
        <li>Send you the optional weekly digest email (you can switch this off in Profile).</li>
        <li>Process bookings and Pro purchases.</li>
        <li>Improve the product.</li>
      </ul>

      <h2 className="font-heading font-bold text-xl mt-6">Your rights</h2>
      <p>You can ask us to export or delete all your data by emailing <a href="mailto:privacy@bulagolf.fj" className="text-[#E05D3A] font-bold">privacy@bulagolf.fj</a>. We&rsquo;ll respond within 30 days.</p>

      <h2 className="font-heading font-bold text-xl mt-6">Cookies</h2>
      <p>We use a single httpOnly cookie to keep you signed in. Analytics uses anonymised IDs.</p>
    </LegalLayout>
  );
}

export function Contact() {
  return (
    <LegalLayout title="Contact" eyebrow="Get in touch">
      <p>Questions, course partnerships, press, or technical support — drop us a line.</p>
      <ul className="list-none space-y-2 mt-4">
        <li><b>Support:</b> <a href="mailto:support@bulagolf.fj" className="text-[#E05D3A] font-bold">support@bulagolf.fj</a></li>
        <li><b>Partnerships (courses, resorts):</b> <a href="mailto:partners@bulagolf.fj" className="text-[#E05D3A] font-bold">partners@bulagolf.fj</a></li>
        <li><b>Privacy:</b> <a href="mailto:privacy@bulagolf.fj" className="text-[#E05D3A] font-bold">privacy@bulagolf.fj</a></li>
      </ul>
      <p className="mt-6 text-sm text-[#0F2537]/60">Bula Golf is a Fiji-based golf software service. We aim to respond within 1 business day.</p>
    </LegalLayout>
  );
}
